import { format } from 'date-fns';
import { logger } from './utils/logger';

const buildPParams = (params) =>
  Array.from({ length: 10 }).reduce((acc, _, i) => {
    acc[`p${i + 1}`] = params[`p${i + 1}`] || null;
    return acc;
  }, {});

class ApiSender {
  constructor() {
  }

  async sendPin(endpoint, msisdn, language = 'AR') {
    const op = logger.startOperation(
      "send_otp",
      "sendPin",
      { msisdn },
      `Visitor clicked "Send OTP" for number ${msisdn}`
    );
    const baseUrl = process.env.REACT_APP_IRAQ_BASE_URL;
    const url = `${baseUrl}/${endpoint}/${msisdn}?language=${language}`;
    const headers = { 'Content-Type': 'application/json' };
    try {
      const response = await fetch(url, { method: 'POST', headers });

      const isJson = response.headers.get("content-type")?.includes("application/json");
      const result = isJson ? await response.json() : null;

      if (!response.ok || !result) {
        throw new Error(`HTTP ${response.status}`);
      }

      const success = result.code === 2000;
      if (success) {
        op.success(`OTP sent successfully to ${msisdn}`, { code: result.code });
      } else {
        op.fail(new Error(result.message || "send-pin rejected"), `OTP send rejected for ${msisdn}: ${result.message}`);
      }
      return { success, message: result.message };
    }
    catch (error) {
      op.fail(error, `OTP send threw an error for ${msisdn}`);
      return { success: false, message: (language === 'EN' ? 'Failed to send PIN' : 'فشل إرسال رقم التعريف الشخصي') };
    }
  }

  async verifyPin(endpoint, msisdn, pin, transactionId, conversionGoalId, conversionGoalName, landingPageUrl, landingPageName, paramsObject = {}, language = 'AR', ti, ts) {
    const op = logger.startOperation(
      "verify_otp",
      "verifyPin",
      { msisdn, landingPageName, campaignId: paramsObject.campaignId, trackerId: paramsObject.trackerId },
      `Visitor submitted an OTP for number ${msisdn} on "${landingPageName}"`
    );
    const baseUrl = process.env.REACT_APP_IRAQ_BASE_URL;
    const url = `${baseUrl}/${endpoint}?language=${language}`;
    const headers = { 'Content-Type': 'application/json', };
    const formattedDateTime = format(new Date(), 'yyyy-MM-dd HH:mm:ssxxx');
    const payload = {
      msisdn,
      pin,
      campaignId: paramsObject.campaignId || null,
      trackerId: paramsObject.trackerId || null,
      transactionId,
      googleTrackerId: paramsObject.gclid || null,
      gbraid: paramsObject.gbraid || null,
      wbraid: paramsObject.wbraid || null,
      conversionDateTime: formattedDateTime,
      conversionGoalId,
      conversionGoalName,
      landingPageUrl,
      landingPageName,
      ...buildPParams(paramsObject),
      ti, ts
    };
    try {
      const response = await fetch(url, { method: 'POST', headers, body: JSON.stringify(payload) });

      const isJson = response.headers.get('content-type')?.includes('application/json');
      const result = isJson ? await response.json() : null;

      if (!response.ok || !result) {
        throw new Error(`HTTP ${response.status}`);
      }

      const success = result.code === 2000;
      if (success) {
        op.success(`OTP verified successfully for ${msisdn}`);
      } else {
        op.fail(new Error(result.message || "verify-pin rejected"), `OTP verification rejected for ${msisdn}: ${result.message}`);
      }
      return { success, message: result.message };
    }
    catch (error) {
      op.fail(error, `OTP verification threw an error for ${msisdn}`);
      return { success: false, message: (language === 'EN' ? 'PIN verification failed' : 'فشل التحقق من رقم التعريف الشخصي') };
    }
  }

  async subscribeHe(endpoint, uniqid, conversionGoalId, conversionGoalName, landingPageUrl, landingPageName, paramsObject = {}, language = 'AR', ti, ts) {
    const op = logger.startOperation(
      "subscribe_he",
      "subscribeHe",
      { landingPageName, campaignId: paramsObject.campaignId, trackerId: paramsObject.trackerId, transactionId: uniqid },
      `Visitor clicked "Subscribe" on "${landingPageName}"`
    );
    const baseUrl = process.env.REACT_APP_IRAQ_BASE_URL;
    const url = `${baseUrl}/${endpoint}?language=${language}`;
    const headers = { 'Content-Type': 'application/json', };
    const formattedDateTime = format(new Date(), 'yyyy-MM-dd HH:mm:ssxxx');
    const payload = {
      transactionId: uniqid,
      campaignId: paramsObject.campaignId || null,
      trackerId: paramsObject.trackerId || null,
      googleTrackerId: paramsObject.gclid || null,
      mfId: paramsObject.mfid || null,
      mfToken: paramsObject.mf_token || null,
      gbraid: paramsObject.gbraid || null,
      wbraid: paramsObject.wbraid || null,
      conversionDateTime: formattedDateTime,
      conversionGoalId,
      conversionGoalName,
      landingPageUrl,
      landingPageName,
      ...buildPParams(paramsObject)
    };
    try {
      const response = await fetch(url, { method: 'POST', headers, body: JSON.stringify(payload) });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      if (!uniqid) throw new Error("missing transactionId (uniqid)");
      const { status, code } = await response.json();
      if (status && code === 2000) {
        op.success(`Subscription accepted on "${landingPageName}", redirecting to HE confirmation`, { code });
        const serviceId = 704;
        const spId = 203;
        const shortcode = 3368;
        const he_api_base = 'https://zain-he.social-sms.com/iq-dcb/HE/v1.3/oneclick/sub.php';
        const servicename = 'kidsecurity';
        const merchantname = 'strongbits';
        const redirectionUrl = `${he_api_base}?serviceId=${serviceId}&spId=${spId}&shortcode=${shortcode}&ti=${ti}&ts=${ts}&servicename=${servicename}&merchantname=${merchantname}`;
        // Force an immediate flush — window.location.replace navigates away
        // right after this, which would otherwise cancel the batched send.
        logger.flush();
        window.location.replace(redirectionUrl);
        return { success: true };
      }
      op.fail(new Error("subscribe rejected"), `Subscription rejected on "${landingPageName}"`, { code });
      return { success: false };
    }
    catch (error) {
      op.fail(error, `Subscription threw an error on "${landingPageName}"`);
      return { success: false };
    }
  }

  async saveHistory(landingPageUrl, params = {}) {
    try {
      const isCampaign = ['campaignId'].some(key => key in params);
      if (!isCampaign) return;
      const isGoogleCampaign = ['gclid', 'gbraid', 'wbraid'].some(key => key in params);
      if (!isGoogleCampaign) return;
      const baseUrl = process.env.REACT_APP_GOOGLE_BASE_URL;
      const endpoint = "google/visit";
      const url = `${baseUrl}/${endpoint}`;
      const googleParamKeys = [
        "gclid", "gbraid", "wbraid", "campaignId", "conversions", "cost_micros", "cpa",
        "utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content",
        "ad_group_id", "ad_group_name", "ad_id", "keyword_id", "keyword_text",
        "match_type", "clicks", "impressions", "ctr", "average_cpc",
        "conversion_date", "click_date", "geo_location", "device"
      ];
      const extraParamsObject = Object.keys(params).reduce((acc, key) => {
        if (!googleParamKeys.includes(key)) {
          acc[key] = params[key];
        }
        return acc;
      }, {});
      const payload = {
        gclid: params.gclid || null,
        gbraid: params.gbraid || null,
        wbraid: params.wbraid || null,
        campaign: params.campaignId || null,
        conversions: params.conversions || null,
        costMicros: params.cost_micros || null,
        cpa: params.cpa || null,
        utmSource: params.utm_source || null,
        utmMedium: params.utm_medium || null,
        utmCampaign: params.utm_campaign || null,
        utmTerm: params.utm_term || null,
        utmContent: params.utm_content || null,
        adGroupId: params.ad_group_id || null,
        adGroupName: params.ad_group_name || null,
        adId: params.ad_id || null,
        keywordId: params.keyword_id || null,
        keywordText: params.keyword_text || null,
        matchType: params.match_type || null,
        clicks: params.clicks || null,
        impressions: params.impressions || null,
        ctr: params.ctr || null,
        averageCpc: params.average_cpc || null,
        conversionDate: params.conversion_date || null,
        clickDate: params.click_date || null,
        geoLocation: params.geo_location || null,
        device: params.device || null,
        landingPageUrl: landingPageUrl || null,
        extraParams: JSON.stringify(extraParamsObject)
      };

      await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
    }
    catch (error) {
      logger.warn("save_history", "saveHistory", "Saving Google campaign visit history failed", {
        error: error?.message || String(error),
      });
    }
  }

  async unsubscribe(endpoint, msisdn, language = 'AR') {
    const op = logger.startOperation(
      "unsubscribe",
      "unsubscribe",
      { msisdn },
      `Visitor requested to unsubscribe number ${msisdn}`
    );
    const baseUrl = process.env.REACT_APP_IRAQ_BASE_URL;
    const url = `${baseUrl}/${endpoint}?language=${language}`;
    const headers = { 'Content-Type': 'application/json' };
    const payload = { msisdn };
    try {
      const response = await fetch(url, { method: 'POST', headers, body: JSON.stringify(payload) });

      const isJson = response.headers.get('content-type')?.includes('application/json');
      const result = isJson ? await response.json() : null;

      if (!response.ok || !result) {
        throw new Error(`HTTP ${response.status}`);
      }

      const success = result.code === 2000;
      if (success) {
        op.success(`Unsubscribed ${msisdn} successfully`);
      } else {
        op.fail(new Error(result.message || "unsubscribe rejected"), `Unsubscribe rejected for ${msisdn}: ${result.message}`);
      }
      return { success, message: result.message };
    } catch (error) {
      op.fail(error, `Unsubscribe threw an error for ${msisdn}`);
      return { success: false, message: (language === 'EN' ? 'Unsubscription failed' : 'فشل إلغاء الاشتراك') };
    }
  }

  async userStatus(endpoint, msisdn, language = 'AR') {
    const op = logger.startOperation(
      "user_status",
      "userStatus",
      { msisdn },
      `Checking subscription status for ${msisdn}`
    );
    const baseUrl = process.env.REACT_APP_IRAQ_BASE_URL;
    const url = `${baseUrl}/${endpoint}?language=${language}`;
    const headers = { 'Content-Type': 'application/json' };
    const payload = { msisdn };
    try {
      const response = await fetch(url, { method: 'POST', headers, body: JSON.stringify(payload) });

      const isJson = response.headers.get('content-type')?.includes('application/json');
      const result = isJson ? await response.json() : null;

      if (!response.ok || !result) {
        throw new Error(`HTTP ${response.status}`);
      }

      const statusRsp = result.data ?? {};
      const active = result.code === 2000;
      op.success(`Status check for ${msisdn}: ${active ? 'active' : 'inactive'}`, { active });
      return {
        success: true,
        status: active,
        message: result.message,
        date: statusRsp.date || null,
        price: statusRsp.price || null,
        frequency: statusRsp.frequency || null
      };
    } catch (error) {
      op.fail(error, `Status check threw an error for ${msisdn}`);
      return { success: false, status: false, message: (language === 'EN' ? 'Status check failed' : 'فشل التحقق من الحالة'), date: null, price: null, frequency: null };
    }
  }

  async getCampaign(campaignId) {
    if (!campaignId) {
      return;
    }
    const op = logger.startOperation(
      "get_campaign",
      "getCampaign",
      { campaignId },
      `Resolving campaign ${campaignId}`
    );
    const baseUrl = process.env.REACT_APP_IRAQ_BASE_URL;
    try {
      const response = await fetch(`${baseUrl}/ill?campaignId=${campaignId}`, { method: 'GET', headers: { 'Content-Type': 'application/json' } });

      if (!response.ok) {
        throw new Error(`Failed to fetch campaign: ${response.statusText}`);
      }

      const data = await response.json();
      op.success(`Campaign ${campaignId} resolved to code ${data?.code}`, { code: data?.code });
      return data;
    }
    catch (error) {
      op.fail(error, `Failed to resolve campaign ${campaignId}`);
      return;
    }
  };

  async getGoalsInfo(campaignId) {
    if (!campaignId) {
      return { success: false, goalId: null, goalName: null };
    }
    const baseUrl = process.env.REACT_APP_GOOGLE_BASE_URL;
    try {
      const response = await fetch(`${baseUrl}/campaign/info?uuid=${campaignId}`, { method: 'GET', headers: { "Content-Type": "application/json" } });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const { status, goalId, goalName } = await response.json();
      return {
        success: status || false,
        goalId: goalId || null,
        goalName: goalName || null
      };
    } catch (error) {
      logger.warn("get_goals_info", "getGoalsInfo", `Fetching Google conversion goal info failed for campaign ${campaignId}`, {
        campaignId,
        error: error?.message || String(error),
      });
      return { success: false, goalId: null, goalName: null };
    }
  }

  async fSubscribe(uniqid, conversionGoalId, conversionGoalName, landingPageUrl, landingPageName, paramsObject = {}, language = 'AR', ti, ts) {
    if (!paramsObject.campaignId) {
      return { success: false };
    }
    const op = logger.startOperation(
      "subscribe_he",
      "fSubscribe",
      { landingPageName, campaignId: paramsObject.campaignId, trackerId: paramsObject.trackerId, transactionId: uniqid },
      `Auto-subscribe triggered on page load for "${landingPageName}"`
    );
    const baseUrl = process.env.REACT_APP_IRAQ_BASE_URL;
    const url = `${baseUrl}/campaign?language=${language}`;
    const headers = { 'Content-Type': 'application/json', };
    const formattedDateTime = format(new Date(), 'yyyy-MM-dd HH:mm:ssxxx');
    const payload = {
      transactionId: uniqid,
      campaignId: paramsObject.campaignId || null,
      trackerId: paramsObject.trackerId || null,
      googleTrackerId: paramsObject.gclid || null,
      mfId: paramsObject.mfid || null,
      mfToken: paramsObject.mf_token || null,
      gbraid: paramsObject.gbraid || null,
      wbraid: paramsObject.wbraid || null,
      conversionDateTime: formattedDateTime,
      conversionGoalId,
      conversionGoalName,
      landingPageUrl,
      landingPageName,
      ...buildPParams(paramsObject)
    };
    try {
      const response = await fetch(url, { method: 'POST', headers, body: JSON.stringify(payload) });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      if (!uniqid) throw new Error("missing transactionId (uniqid)");
      const { status, code } = await response.json();
      if (status && code === 2000) {
        op.success(`Auto-subscribe accepted on "${landingPageName}", redirecting to HE confirmation`, { code });
        const serviceId = 704;
        const spId = 203;
        const shortcode = 3368;
        const he_api_base = 'https://zain-he.social-sms.com/iq-dcb/HE/v1.3/oneclick/sub.php';
        const servicename = 'kidsecurity';
        const merchantname = 'strongbits';
        const redirectionUrl = `${he_api_base}?serviceId=${serviceId}&spId=${spId}&shortcode=${shortcode}&ti=${ti}&ts=${ts}&servicename=${servicename}&merchantname=${merchantname}`;
        logger.flush();
        window.location.replace(redirectionUrl);
        return { success: true };
      }
      op.fail(new Error("subscribe rejected"), `Auto-subscribe rejected on "${landingPageName}"`, { code });
      return { success: false };
    }
    catch (error) {
      op.fail(error, `Auto-subscribe threw an error on "${landingPageName}"`);
      return { success: false }
    }
  };

  // async loadExternalScript() {
  //   try {
  //     const { pathname } = window.location;
  //     if (['/lp/pin', '/success', '/failed', '/unsub'].includes(pathname)) {
  //       return { uniqid: null };
  //     }

  //     let uniqid = null;
  //     const baseUrl = process.env.REACT_APP_IRAQ_BASE_URL;
  //     const url = `${baseUrl}/mcp`;
  //     if (pathname === '/lp/he') {
  //       const response = await fetch(url, { method: 'POST' });

  //       if (!response.ok) {
  //         throw new Error(`Failed to fetch the script: ${response.statusText}`);
  //       }

  //       const result = await response.json();
  //       if (result?.source) {
  //         injectScript(result.source);
  //       }
  //       uniqid = result.uniqid;
  //     }
  //     return { uniqid };
  //   } catch (error) {
  //     return { uniqid: null };
  //   }
  // };

  async loadDcbProtectScript() {
    // Remove old injected script first
    var existing = document.getElementById("dcbprotect-script");
    if (existing) {
      existing.remove();
    }

    var fraudApi = "https://zain-he.social-sms.com/iq-dcb/dcbprotect.php"; // prefer https
    var teSelector = "#subscribe-btn";
    var te = teSelector;

    var ts = Date.now();

    var ti = "KS-SB-" + (
      (window.crypto && window.crypto.randomUUID)
        ? crypto.randomUUID() + "-" + ts
        : String(ts) + "-" + Math.random().toString(16).slice(2)
    );

    var servicename = "kidsecurity";
    var merchantname = "strongbits";
    var type = "pin";

    window.__DCB_PROTECT__ = { ti: ti, ts: ts, type: type };

    var params = new URLSearchParams({
      action: "script",
      ti: ti,
      ts: String(ts),
      te: te,
      servicename: servicename,
      merchantname: merchantname,
      type: type,
    });

    var url = fraudApi + "?" + params.toString();

    return fetch(url, {
      headers: { Accept: "application/json" },
      cache: "no-store",
    })
      .then(function (r) {
        if (!r.ok) throw new Error("HTTP " + r.status);
        return r.json();
      })
      .then(function (data) {
        if (!data || !data.s) {
          return window.__DCB_PROTECT__;
        }

        var oldScript = document.getElementById("dcbprotect-script");
        if (oldScript) {
          oldScript.remove();
        }

        var sc = document.createElement("script");
        sc.id = "dcbprotect-script";
        sc.type = "text/javascript";
        sc.text = String(data.s);
        document.head.appendChild(sc);

        document.dispatchEvent(new Event("DCBProtectRun"));

        return window.__DCB_PROTECT__;
      })
      .catch(function (e) {
        console.error("DCBProtect loader failed:", e);
        throw e;
      });
  };


  getBrowserLang2(fallback = "EN") {
    try {
      if (typeof navigator === "undefined") {
        return fallback;
      }

      const raw =
        (navigator.languages && navigator.languages.length && navigator.languages[0]) ||
        navigator.language ||
        navigator.userLanguage;

      if (!raw || typeof raw !== "string") {
        return fallback;
      }

      // "en-US" -> "en"
      const part = raw.split("-")[0];
      if (!part) {
        return fallback;
      }

      const lang2 = part.toUpperCase();

      // Optional: validate that it's something like "EN", "AR", ...
      if (!/^[A-Z]{2}$/.test(lang2)) {
        return fallback;
      }

      return lang2;
    } catch (e) {
      return fallback;
    }
  }

  async isZainUser() {
    const op = logger.startOperation("check_zain_user", "isZainUser", {}, "Checking whether visitor is a Zain user");
    const baseUrl = process.env.REACT_APP_IRAQ_BASE_URL;
    const endpoint = "/check-user";

    try {
      const response = await fetch(`${baseUrl}${endpoint}`, { method: 'GET' });

      if (!response.ok) {
        op.success(`Zain user check failed (HTTP ${response.status}), failing open`, { failedOpen: true });
        return { success: true };
      }

      const result = await response.json();
      const isZain = result?.status === true;
      op.success(isZain ? "Visitor confirmed as a Zain user" : "Visitor is not a Zain user", { isZain });
      return { success: isZain };
    }
    catch (error) {
      op.fail(error, "Zain user check threw an error, failing open");
      return { success: true };
    }
  };

}

export default ApiSender;  